<template>
  <div>
    <div class="row">
      <div class="col-md-12">
        <KTCodePreview v-bind:title="pageTitle">
          <template v-slot:preview>
            <!-- Bộ lọc (§7.7) -->
            <div class="report-filter mb-5">
              <b-row class="align-items-end">
                <b-col md="2">
                  <label class="report-filter__label">Từ ngày</label>
                  <date-picker
                    placeholder="Từ ngày"
                    class="form-control form-control-sm"
                    :config="dpConfigs.date"
                    v-model="filter.fromDate"
                  ></date-picker>
                </b-col>
                <b-col md="2">
                  <label class="report-filter__label">Đến ngày</label>
                  <date-picker
                    placeholder="Đến ngày"
                    class="form-control form-control-sm"
                    :config="dpConfigs.date"
                    v-model="filter.toDate"
                  ></date-picker>
                </b-col>
                <b-col md="4">
                  <label class="report-filter__label">{{ scopeLabel }}</label>
                  <!-- Ngành cửa hàng: tìm cửa hàng -->
                  <Autosuggest
                    v-if="!isCrReport"
                    :model.sync="filter.storeText"
                    :suggestions="filteredStores"
                    placeholder="cửa hàng"
                    :limit="10"
                    @select="onSelectedStore"
                    @change="onInputStore"
                    suggestionName="suggestionName"
                  />
                  <!-- CR: chọn bộ phận -->
                  <b-form-select
                    v-else
                    size="sm"
                    v-model="filter.departmentId"
                    :options="departmentOptions"
                    value-field="id"
                    text-field="name"
                  >
                    <template v-slot:first>
                      <b-form-select-option :value="null"
                        >-- Chọn bộ phận --</b-form-select-option
                      >
                    </template>
                  </b-form-select>
                </b-col>
                <b-col md="4" class="d-flex align-items-center report-filter__actions">
                  <b-button
                    variant="primary"
                    size="sm"
                    class="px-6 mr-4"
                    :disabled="onLoading"
                    @click="onFilter"
                    >Lọc</b-button
                  >
                  <b-form-checkbox
                    v-model="filter.onlyWithCommission"
                    size="sm"
                    @change="onFilter"
                  >
                    Ẩn dòng 0đ
                  </b-form-checkbox>
                </b-col>
              </b-row>
            </div>

            <!-- Chương trình đang áp dụng trong kỳ -->
            <div v-if="programs.length" class="col-md-12 mb-4 pl-0">
              <span class="text-muted mr-2"
                >Chương trình áp dụng trong kỳ:</span
              >
              <span
                v-for="program in programs"
                :key="program.id"
                class="label label-inline label-light-primary font-weight-bold mr-2 mb-1"
                :title="programPeriod(program)"
                >{{ program.name }}</span
              >
            </div>

            <!-- Khối tổng quan (§7.4): tổng thưởng nổi bật, bên phải là các khoản cấu thành -->
            <div v-if="hasSearched" class="report-summary mb-5">
              <div class="report-summary__total">
                <div class="report-summary__label">Tổng thưởng</div>
                <div
                  class="report-summary__total-value"
                  :class="amountClass(totals.totalAmount)"
                >
                  {{ displayAmount(totals.totalAmount) }}
                </div>
                <div class="report-summary__meta">
                  {{ displayQuantity(totals.employeeCount) }} nhân sự ·
                  {{ displayQuantity(totals.quantity) }} sản phẩm
                </div>
              </div>
              <div class="report-summary__parts">
                <div
                  v-for="part in summaryParts"
                  :key="part.key"
                  class="report-summary__part"
                >
                  <div class="report-summary__label">
                    <span class="report-summary__dot" :class="part.dot"></span>
                    {{ part.label }}
                  </div>
                  <div class="report-summary__part-value">{{ part.value }}</div>
                </div>
              </div>
            </div>

            <!-- Bảng tổng hợp — không phân trang (§7.12) -->
            <b-table
              :items="displayItems"
              :fields="fields"
              bordered
              hover
              :busy="onLoading"
              show-empty
              :empty-text="emptyText"
              class="report-table"
            >
              <template v-slot:table-busy>
                <vcl-table :speed="5" :animate="true" :columns="10"></vcl-table>
              </template>
              <template v-slot:cell(count)="row">
                <span>{{ row.index + 1 }}</span>
              </template>
              <template v-slot:cell(employeeName)="row">
                <div class="font-weight-bold text-dark">
                  {{ row.item.employeeName }}
                </div>
                <div class="text-muted small">
                  {{ row.item.employeeCode }}
                  <template v-if="row.item.jobTitleName">
                    · {{ row.item.jobTitleName }}</template
                  >
                </div>
              </template>
              <template v-slot:cell(quantity)="row">
                <span>{{ displayQuantity(row.item.quantity) }}</span>
              </template>
              <template v-slot:cell(commissionAmount)="row">
                <span :class="moneyClass(row.item.commissionAmount)">{{
                  displayAmount(row.item.commissionAmount)
                }}</span>
              </template>
              <template v-slot:cell(adhocAmount)="row">
                <span :class="moneyClass(row.item.adhocAmount)">{{
                  displayAmount(row.item.adhocAmount)
                }}</span>
              </template>
              <template v-slot:cell(bonusAmount)="row">
                <span :class="moneyClass(row.item.bonusAmount)">{{
                  displayAmount(row.item.bonusAmount)
                }}</span>
              </template>
              <template v-slot:cell(minusAmount)="row">
                <span :class="moneyClass(row.item.minusAmount)">{{
                  displayAmount(row.item.minusAmount)
                }}</span>
              </template>
              <template v-slot:cell(totalAmount)="row">
                <span
                  class="report-total"
                  :class="amountClass(row.item.totalAmount)"
                  >{{ displayAmount(row.item.totalAmount) }}</span
                >
              </template>
              <template v-slot:cell(actions)="row">
                <b-button
                  size="sm"
                  variant="link"
                  class="report-detail-btn"
                  title="Xem chi tiết hoa hồng của nhân viên"
                  @click="goToDetail(row.item)"
                >
                  <i class="fas fa-eye text-primary"></i>
                </b-button>
              </template>

              <!-- Dòng tổng cộng -->
              <template v-slot:custom-foot>
                <tr class="report-table__foot" v-if="displayItems.length">
                  <td colspan="2" class="text-right font-weight-bold">
                    Tổng cộng
                  </td>
                  <td class="text-center font-weight-bold">
                    {{ displayQuantity(footTotals.quantity) }}
                  </td>
                  <td
                    class="text-right font-weight-bold"
                    :class="amountClass(footTotals.commissionAmount)"
                  >
                    {{ displayAmount(footTotals.commissionAmount) }}
                  </td>
                  <td
                    class="text-right font-weight-bold"
                    :class="amountClass(footTotals.adhocAmount)"
                  >
                    {{ displayAmount(footTotals.adhocAmount) }}
                  </td>
                  <td
                    class="text-right font-weight-bold"
                    :class="amountClass(footTotals.bonusAmount)"
                  >
                    {{ displayAmount(footTotals.bonusAmount) }}
                  </td>
                  <td class="text-right font-weight-bold">
                    {{ displayAmount(footTotals.minusAmount) }}
                  </td>
                  <td
                    class="text-right font-weight-bold"
                    :class="amountClass(footTotals.totalAmount)"
                  >
                    {{ displayAmount(footTotals.totalAmount) }}
                  </td>
                  <!-- ô trống khớp với cột thao tác, giữ dòng tổng không lệch cột -->
                  <td></td>
                </tr>
              </template>
            </b-table>

          </template>
        </KTCodePreview>
      </div>
    </div>
  </div>
</template>

<script>
import KTCodePreview from '@/view/content/CodePreview.vue';
import Autosuggest from '@/view/base/auto-sugguest/AutoSuggest.vue';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import ApiService from '@/core/services/api.service';
import moment from 'moment';
import datePicker from 'vue-bootstrap-datetimepicker';
import 'pc-bootstrap4-datetimepicker/build/css/bootstrap-datetimepicker.css';
import { VclTable } from 'vue-content-loading';
import { makeToastFaile } from '@/utils/common';
import { COMMISSION_REPORT_TYPE_NAME } from './commission.constants';
import commissionReportMixin, {
  DATE_INPUT_FORMAT,
  thStyle,
} from './commissionReport.mixin';

const EMPTY_TOTALS = Object.freeze({
  employeeCount: 0,
  quantity: 0,
  commissionAmount: 0,
  adhocAmount: 0,
  bonusAmount: 0,
  minusAmount: 0,
  totalAmount: 0,
});

export default {
  components: {
    KTCodePreview,
    Autosuggest,
    VclTable,
    datePicker,
  },
  mixins: [commissionReportMixin],
  data() {
    return {
      onLoading: false,
      hasSearched: false,

      items: [],
      programs: [],
      totals: { ...EMPTY_TOTALS },
      period: null,
      store: null,

      storeOptions: [],
      departmentOptions: [],
      filteredStores: [],

      filter: {
        fromDate: moment().startOf('month').format(DATE_INPUT_FORMAT),
        toDate: moment().format(DATE_INPUT_FORMAT),
        storeId: null,
        storeText: '',
        departmentId: null,
        // Mặc định ẩn dòng Tổng thưởng = 0 cho gọn.
        // Dòng ÂM vẫn hiện (vd bị trừ từ cộng/trừ khác) — đó là tiền thật cần thấy.
        onlyWithCommission: true,
      },

      fields: [
        {
          key: 'count',
          label: 'STT',
          thStyle: thStyle('5%'),
          tdClass: 'text-center text-muted',
        },
        {
          key: 'employeeName',
          label: 'Nhân viên',
          thStyle: thStyle('27%'),
        },
        {
          key: 'quantity',
          label: 'Số lượng',
          thStyle: thStyle('8%'),
          tdClass: 'text-center',
        },
        {
          key: 'commissionAmount',
          label: 'Hoa hồng',
          thStyle: thStyle('12%'),
          tdClass: 'text-right',
        },
        {
          key: 'adhocAmount',
          label: 'AdHoc',
          thStyle: thStyle('10%'),
          tdClass: 'text-right',
        },
        {
          key: 'bonusAmount',
          label: 'Cộng khác',
          thStyle: thStyle('10%'),
          tdClass: 'text-right',
        },
        {
          key: 'minusAmount',
          label: 'Trừ khác',
          thStyle: thStyle('10%'),
          tdClass: 'text-right',
        },
        {
          key: 'totalAmount',
          label: 'Tổng thưởng',
          thStyle: thStyle('12%'),
          tdClass: 'text-right',
        },
        {
          key: 'actions',
          label: '',
          thStyle: thStyle('6%'),
          tdClass: 'text-center',
        },
      ],
    };
  },
  computed: {
    pageTitle() {
      return (
        COMMISSION_REPORT_TYPE_NAME[this.reportType] || 'Báo cáo hoa hồng'
      );
    },
    /**
     * §7.7: lọc "chỉ hiện người có hoa hồng" CHỈ ảnh hưởng hiển thị.
     * BE cũng nhận tham số này, nhưng vẫn lọc lại phía FE để đổi checkbox
     * không phải gọi lại API.
     */
    displayItems() {
      if (!this.filter.onlyWithCommission) {
        return this.items;
      }
      return this.items.filter((item) => Number(item.totalAmount) !== 0);
    },
    /** Tổng cuối bảng — tính từ đúng các dòng đang hiển thị */
    footTotals() {
      return this.displayItems.reduce(
        (acc, item) => ({
          quantity: acc.quantity + (Number(item.quantity) || 0),
          commissionAmount:
            acc.commissionAmount + (Number(item.commissionAmount) || 0),
          adhocAmount: acc.adhocAmount + (Number(item.adhocAmount) || 0),
          bonusAmount: acc.bonusAmount + (Number(item.bonusAmount) || 0),
          minusAmount: acc.minusAmount + (Number(item.minusAmount) || 0),
          totalAmount: acc.totalAmount + (Number(item.totalAmount) || 0),
        }),
        {
          quantity: 0,
          commissionAmount: 0,
          adhocAmount: 0,
          bonusAmount: 0,
          minusAmount: 0,
          totalAmount: 0,
        },
      );
    },
    /** Các khoản cấu thành Tổng thưởng — lấy từ totals do BE trả về */
    summaryParts() {
      const totals = this.totals || EMPTY_TOTALS;
      return [
        { key: 'commissionAmount', label: 'Hoa hồng', dot: 'is-commission' },
        { key: 'adhocAmount', label: 'AdHoc', dot: 'is-adhoc' },
        { key: 'bonusAmount', label: 'Cộng khác', dot: 'is-bonus' },
        { key: 'minusAmount', label: 'Trừ khác', dot: 'is-minus' },
      ].map((part) => ({
        ...part,
        value: this.displayAmount(totals[part.key]),
      }));
    },
    emptyText() {
      if (!this.hasSearched) {
        return `Chọn kỳ và ${this.scopeLabel.toLowerCase()} rồi bấm Lọc để xem báo cáo`;
      }
      if (this.filter.onlyWithCommission && this.items.length) {
        return 'Không có nhân sự nào phát sinh hoa hồng trong kỳ';
      }
      return 'Không có dữ liệu hoa hồng trong kỳ đã chọn';
    },
  },
  watch: {
    '$route.name': function () {
      this.resolveReportType();
      this.setBreadcrumb();
      this.resetResult();
      this.fetchScopeOptions();
    },
  },
  created() {
    this.resolveReportType();
    this.setBreadcrumb();
    this.restoreFilterFromQuery();
    this.fetchScopeOptions();
  },
  methods: {
    /**
     * Đọc lại filter từ query string để F5 / chia sẻ link không mất điều kiện lọc.
     * Cửa hàng cần chờ danh sách tải xong mới gán được tên -> xử lý ở restoreStoreFromQuery.
     */
    restoreFilterFromQuery() {
      const query = this.$route.query || {};
      if (query.fromDate) {
        this.filter.fromDate = query.fromDate;
      }
      if (query.toDate) {
        this.filter.toDate = query.toDate;
      }
      if (query.onlyWithCommission !== undefined) {
        this.filter.onlyWithCommission = query.onlyWithCommission !== 'false';
      }
      if (this.isCrReport && query.departmentId) {
        this.filter.departmentId = Number(query.departmentId);
        this.$nextTick(() => this.fetchReport());
      } else if (!this.isCrReport && query.storeId) {
        this.filter.storeId = Number(query.storeId);
      }
    },
    /** Gán lại TÊN cửa hàng sau khi danh sách tải xong, rồi tự tải báo cáo */
    restoreStoreFromQuery() {
      if (this.isCrReport || !this.filter.storeId) {
        return;
      }
      const store = this.storeOptions.find(
        (item) => item.id === this.filter.storeId,
      );
      if (store) {
        this.filter.storeText = store.suggestionName;
        this.fetchReport();
      }
    },
    /** Ghi filter hiện tại lên URL */
    syncFilterToQuery() {
      const query = {};
      if (this.filter.fromDate) {
        query.fromDate = this.filter.fromDate;
      }
      if (this.filter.toDate) {
        query.toDate = this.filter.toDate;
      }
      if (!this.filter.onlyWithCommission) {
        query.onlyWithCommission = 'false';
      }
      if (this.isCrReport) {
        if (this.filter.departmentId) {
          query.departmentId = this.filter.departmentId;
        }
      } else if (this.filter.storeId) {
        query.storeId = this.filter.storeId;
      }
      this.$router.replace({ query }).catch(() => {});
    },
    setBreadcrumb() {
      this.$store.dispatch(SET_BREADCRUMB, [
        { title: 'Hoa hồng V2' },
        { title: this.pageTitle },
      ]);
    },
    programPeriod(program) {
      const to = program.endDate
        ? this.displayDate(program.endDate)
        : 'không giới hạn';
      return `Áp dụng: ${this.displayDate(program.startDate)} - ${to}`;
    },
    /** Số 0 làm mờ để mắt bám vào các ô có tiền; số âm vẫn đỏ */
    moneyClass(value) {
      return Number(value) === 0 ? 'text-muted' : this.amountClass(value);
    },
    resetResult() {
      this.items = [];
      this.programs = [];
      this.totals = { ...EMPTY_TOTALS };
      this.period = null;
      this.store = null;
      this.hasSearched = false;
      this.filter.storeId = null;
      this.filter.storeText = '';
      this.filter.departmentId = null;
    },

    /* ------------------------ Nạp danh sách phạm vi ----------------------- */
    fetchScopeOptions() {
      if (this.isCrReport) {
        this.fetchDepartments();
      } else {
        this.fetchStores();
      }
    },
    fetchStores() {
      ApiService.setHeader();
      ApiService.get('/stores/getStores')
        .then(({ data }) => {
          this.storeOptions = (data.data || []).map((store) => ({
            id: store.id,
            name: store.name,
            shortName: store.shortName || '',
            suggestionName: store.shortName
              ? `${store.shortName} (${store.name})`
              : store.name,
          }));
          // Dựng sẵn danh sách gợi ý để bấm vào ô là thấy ngay, không phải gõ mới hiện
          this.filteredStores = this.filterStoreOptions('');
          // Khôi phục cửa hàng đã chọn từ URL (F5 không mất filter)
          this.restoreStoreFromQuery();
        })
        .catch(() => {
          this.storeOptions = [];
          makeToastFaile('Không tải được danh sách cửa hàng!');
        });
    },
    fetchDepartments() {
      ApiService.setHeader();
      ApiService.query('employees/departments')
        .then(({ data }) => {
          this.departmentOptions = data.data || [];
        })
        .catch(() => {
          this.departmentOptions = [];
          makeToastFaile('Không tải được danh sách bộ phận!');
        });
    },
    filterStoreOptions(text) {
      const keyword = (text || '').toLowerCase();
      return this.storeOptions
        .filter(
          (store) =>
            String(store.name).toLowerCase().indexOf(keyword) > -1 ||
            String(store.shortName).toLowerCase().indexOf(keyword) > -1,
        )
        .slice(0, 10);
    },
    onInputStore(text) {
      // Gõ khác tên cửa hàng đang chọn là bỏ lựa chọn cũ
      const selected = this.storeOptions.find(
        (store) => store.id === this.filter.storeId,
      );
      if (!selected || selected.suggestionName !== text) {
        this.filter.storeId = null;
      }
      this.filteredStores = this.filterStoreOptions(text);
    },
    onSelectedStore(option) {
      this.filter.storeId = option.item.id;
      this.filter.storeText = option.item.suggestionName;
    },

    /* ------------------------------ Tải dữ liệu --------------------------- */
    onFilter() {
      const rangeError = this.validateDateRange(
        this.filter.fromDate,
        this.filter.toDate,
      );
      if (rangeError) {
        makeToastFaile(rangeError);
        return;
      }
      if (this.isCrReport && !this.filter.departmentId) {
        makeToastFaile('Vui lòng chọn bộ phận cần xem báo cáo!');
        return;
      }
      if (!this.isCrReport && !this.filter.storeId) {
        makeToastFaile('Vui lòng chọn cửa hàng cần xem báo cáo!');
        return;
      }
      this.syncFilterToQuery();
      this.fetchReport();
    },
    fetchReport() {
      this.onLoading = true;

      const params = {
        type: this.reportType,
        fromDate: this.toApiDate(this.filter.fromDate),
        toDate: this.toApiDate(this.filter.toDate),
        onlyWithCommission: this.filter.onlyWithCommission,
      };
      if (this.isCrReport) {
        params.departmentId = this.filter.departmentId;
      } else {
        params.storeId = this.filter.storeId;
      }

      ApiService.setHeader();
      ApiService.query('commission-reports', { params })
        .then(({ data }) => {
          const result = data.data || {};
          this.items = result.items || [];
          this.programs = result.programs || [];
          this.totals = { ...EMPTY_TOTALS, ...(result.totals || {}) };
          this.period = result.period || null;
          this.store = result.store || null;
          this.hasSearched = true;
        })
        .catch((error) => {
          this.items = [];
          this.programs = [];
          this.totals = { ...EMPTY_TOTALS };
          this.hasSearched = true;
          makeToastFaile(
            this.getErrorMessage(error, 'Không tải được báo cáo hoa hồng!'),
          );
        })
        .finally(() => {
          this.onLoading = false;
        });
    },

    /* ---------------------- Sang màn chi tiết nhân viên ------------------- */
    goToDetail(item) {
      if (!item || !item.employeeId) {
        return;
      }
      const params = {
        type: this.reportType,
        employeeId: item.employeeId,
        fromDate: this.toApiDate(this.filter.fromDate),
        toDate: this.toApiDate(this.filter.toDate),
      };
      if (this.isCrReport) {
        if (this.filter.departmentId) {
          params.departmentId = this.filter.departmentId;
        }
      } else {
        const storeId = item.storeId || this.filter.storeId;
        if (storeId) {
          params.storeId = storeId;
        }
      }
      this.$router.push({ name: 'commission-report-detail', query: params });
    },
  },
};
</script>

<style scoped>
.table th,
.table td {
  padding: 0.75rem;
  vertical-align: middle;
  border-top: 1px solid #ecf0f3;
}

.report-table__foot td {
  background-color: #f3f6f9;
  border-top: 2px solid #dfe3e8;
}

.report-total {
  font-size: 1.05rem;
  font-weight: 600;
  color: #181c32;
}

.report-filter {
  background-color: #f8f9fb;
  border: 1px solid #ecf0f3;
  border-radius: 0.42rem;
  padding: 1rem 1.25rem;
}

.report-filter__label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #7e8299;
  margin-bottom: 0.3rem;
}

.report-filter__actions {
  min-height: calc(1.35em + 1.1rem + 2px);
}

.report-summary {
  display: flex;
  flex-wrap: wrap;
  border: 1px solid #ecf0f3;
  border-radius: 0.42rem;
  overflow: hidden;
}

.report-summary__total {
  flex: 0 0 280px;
  padding: 1.25rem 1.5rem;
  background-color: #f1faff;
  border-right: 1px solid #ecf0f3;
}

.report-summary__total-value {
  font-size: 1.9rem;
  font-weight: 700;
  line-height: 1.2;
  color: #187de4;
}

.report-summary__meta {
  margin-top: 0.4rem;
  font-size: 0.85rem;
  color: #7e8299;
}

.report-summary__parts {
  flex: 1 1 360px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
}

.report-summary__part {
  padding: 0.9rem 1.5rem;
  border-bottom: 1px solid #ecf0f3;
  border-right: 1px solid #ecf0f3;
}

.report-summary__part:nth-child(2n) {
  border-right: 0;
}

.report-summary__part:nth-last-child(-n + 2) {
  border-bottom: 0;
}

.report-summary__label {
  font-size: 0.85rem;
  color: #7e8299;
  margin-bottom: 0.3rem;
}

.report-summary__part-value {
  font-size: 1.15rem;
  font-weight: 600;
  color: #181c32;
}

.report-summary__dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 0.35rem;
}

.report-summary__dot.is-commission {
  background-color: #3699ff;
}

.report-summary__dot.is-adhoc {
  background-color: #8950fc;
}

.report-summary__dot.is-bonus {
  background-color: #1bc5bd;
}

.report-summary__dot.is-minus {
  background-color: #f64e60;
}
</style>

<style>
.report-detail-btn {
  padding: 0.25rem 0.6rem;
  font-size: 0.75rem;
}
</style>
